import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useChartStore } from '@/store/chartStore';
import { useAuthStore } from '@/store/authStore';
import { AuthProvider } from '@/components/Auth/AuthProvider';
import { ReturnPathTracker } from '@/components/Auth/ReturnPathTracker';
import { WebMcpBridge } from '@/webmcp/WebMcpBridge';

// Each page is its own chunk. The chart page and its layout are the heavy ones (the charting
// engine and the macro charts): the home page, sector pages and text pages must not download them.
const loadAppLayout = () => import('@/components/Layout/AppLayout').then((m) => ({ default: m.AppLayout }));
const loadChartPage = () => import('@/pages/ChartPage').then((m) => ({ default: m.ChartPage }));
const AppLayout = lazy(loadAppLayout);
const ChartPage = lazy(loadChartPage);
// A chart URL needs both. The page renders inside the layout, so left to lazy() the second
// download would only start once the first had arrived; ask for them together instead.
if (window.location.pathname.startsWith('/chart/')) {
  void loadAppLayout();
  void loadChartPage();
}
const ResetPasswordPage = lazy(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const CompleteSignupPage = lazy(() =>
  import('@/pages/CompleteSignupPage').then((m) => ({ default: m.CompleteSignupPage })),
);
const OAuthConsentPage = lazy(() =>
  import('@/pages/OAuthConsentPage').then((m) => ({ default: m.OAuthConsentPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsPage = lazy(() => import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })));
const SectorsPage = lazy(() => import('@/pages/SectorsPage').then((m) => ({ default: m.SectorsPage })));
const SectorPage = lazy(() => import('@/pages/SectorPage').then((m) => ({ default: m.SectorPage })));
const McpPage = lazy(() => import('@/pages/McpPage').then((m) => ({ default: m.McpPage })));
const AboutPage = lazy(() => import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const MethodologyPage = lazy(() =>
  import('@/pages/MethodologyPage').then((m) => ({ default: m.MethodologyPage })),
);
const UpgradeModal = lazy(() =>
  import('@/components/Auth/UpgradeModal').then((m) => ({ default: m.UpgradeModal })),
);

export default function App() {
  const theme              = useChartStore((s) => s.theme);
  const upgradeModalOpen   = useAuthStore((s) => s.upgradeModalOpen);
  const openUpgradeModal   = useAuthStore((s) => s.openUpgradeModal);
  const closeUpgradeModal  = useAuthStore((s) => s.closeUpgradeModal);

  // Captured once at load — a `?promo=CODE` deep link (e.g. from a campaign
  // email/ad) auto-opens the Upgrade modal with the code pre-applied.
  const [promoParam] = useState(() => new URLSearchParams(window.location.search).get('promo'));

  useEffect(() => {
    if (!promoParam) return;
    openUpgradeModal();
    // Strip it from the visible URL so a refresh/share doesn't re-trigger it.
    const url = new URL(window.location.href);
    url.searchParams.delete('promo');
    window.history.replaceState({}, '', url.toString());
  }, [promoParam, openUpgradeModal]);

  // Apply theme class to <html> + swap favicons
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    // Swap favicon based on app theme
    // logo-dark-* = icon designed for dark theme (light/white icon on dark chrome)
    // logo-light-* = icon designed for light theme (dark icon on light chrome)
    const variant = theme;
    const sizes: [string, string][] = [
      ['favicon-16', '16x16'],
      ['favicon-32', '32x32'],
      ['favicon-128', '128x128'],
    ];
    sizes.forEach(([id, size]) => {
      const link = document.getElementById(id) as HTMLLinkElement | null;
      if (link) link.href = `/logo-${variant}-${size}.png`;
    });
    const appleLink = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (appleLink) appleLink.href = `/logo-${variant}-180x180.png`;
  }, [theme]);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ReturnPathTracker />
        <WebMcpBridge />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/welcome" element={<CompleteSignupPage />} />
            <Route path="/oauth/consent" element={<OAuthConsentPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sectors" element={<SectorsPage />} />
            <Route path="/sector/:slug" element={<SectorPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/mcp" element={<McpPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route element={<AppLayout />}>
              <Route path="/chart/:symbol" element={<ChartPage />} />
              <Route path="*" element={<Navigate to="/chart/ASI" replace />} />
            </Route>
          </Routes>
          {upgradeModalOpen && <UpgradeModal onClose={closeUpgradeModal} initialPromoCode={promoParam ?? undefined} />}
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
